<template>
  <section ref="root" class="flex flex-col gap-3.5 rounded-3xl border border-edge bg-panel p-4 sm:gap-4 sm:p-6">
    <div class="flex flex-wrap items-baseline justify-between gap-x-2">
      <h2 class="whitespace-nowrap font-display text-[22px] font-extrabold sm:text-[26px]">Wofür gezahlt wird</h2>
      <span v-if="total > 0" class="text-xs text-slate-400 sm:text-sm">{{ hint }}</span>
    </div>

    <p v-if="total === 0" class="py-6 text-center text-slate-400">Noch nichts zu zahlen gewesen.</p>

    <template v-else>
      <div role="img" :aria-label="ariaLabel" class="flex h-[18px] gap-0.5 overflow-hidden rounded-full bg-edge sm:h-[22px]">
        <div
          v-for="slice in slices"
          :key="slice.type"
          class="motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out"
          :style="{ width: grown ? slice.width : '0%', backgroundColor: slice.color }"
        ></div>
      </div>
      <ul class="grid grid-cols-1 gap-y-0.5">
        <li
          v-for="slice in slices"
          :key="slice.type"
          class="relative"
          :class="{ 'z-20': active === slice.type }"
          @mouseenter="hoverOpen(slice.type)"
          @mouseleave="hoverClose(slice.type)"
        >
          <button
            type="button"
            @click="toggle(slice.type)"
            :aria-expanded="active === slice.type"
            :aria-controls="`category-details-${slice.index}`"
            class="flex w-full items-center gap-2 rounded-lg py-1 text-left transition-colors hover:bg-edge/50 sm:px-1"
            :class="{ 'bg-edge/50': active === slice.type }"
          >
            <span class="h-2.5 w-2.5 flex-none rounded-[3px]" :style="{ backgroundColor: slice.color }"></span>
            <span class="min-w-0 flex-1 truncate text-sm sm:text-[15px]">{{ slice.type }}</span>
            <span class="text-sm font-semibold text-slate-300 sm:text-[15px]">{{ slice.share }}</span>
          </button>

          <!-- Aufteilung nach Mitglied -->
          <transition name="pop">
            <div
              v-if="active === slice.type"
              :id="`category-details-${slice.index}`"
              role="region"
              :aria-label="`${slice.type} nach Mitglied`"
              class="absolute inset-x-0 top-full mt-1.5 flex flex-col gap-3 rounded-2xl border border-edge bg-night p-3.5 shadow-2xl shadow-black/50"
            >
              <div class="flex items-baseline justify-between gap-2">
                <span class="text-sm font-semibold">{{ slice.type }}</span>
                <span class="text-xs text-slate-400">{{ formatEuro(slice.value) }}</span>
              </div>
              <div class="flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-edge" aria-hidden="true">
                <div
                  v-for="member in slice.members"
                  :key="member.name"
                  :style="{ width: member.width, backgroundColor: member.color }"
                ></div>
              </div>
              <ul class="flex flex-col gap-1.5">
                <li v-for="member in slice.members" :key="member.name" class="flex items-center gap-2 text-sm">
                  <span class="h-2.5 w-2.5 flex-none rounded-[3px]" :style="{ backgroundColor: member.color }"></span>
                  <span class="min-w-0 flex-1 truncate">{{ member.name }}</span>
                  <span class="text-xs tabular-nums text-slate-400">{{ member.count }}×</span>
                  <span class="w-14 text-right font-semibold tabular-nums">{{ formatEuro(member.value) }}</span>
                </li>
              </ul>
            </div>
          </transition>
        </li>
      </ul>
    </template>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Punishment from '@/types/Punischment';
import PunishmentCategory from '@/types/PunishmentCategory';
import { FineCategory } from '@/services/SeasonService';
import { categoryColorMap, formatEuro } from '@/utils/format';
import { useGrow } from '@/composables/useGrow';
import { useDetailPopover } from '@/composables/useDetailPopover';

const props = defineProps<{
  categories: PunishmentCategory[];
  fines: Punishment[];
  details: Record<string, FineCategory[]>;
}>();

const total = computed(() => props.categories.reduce((sum, c) => sum + c.value, 0));

const colors = computed(() => categoryColorMap(props.categories));

const memberColors = computed(() => Object.fromEntries(props.fines.map((f) => [f.name, f.color])));

// `details` ist nach Mitglied gruppiert, hier wird daraus Kategorie -> Mitglieder
const membersOf = (type: string) => {
  const members = Object.entries(props.details).flatMap(([name, categories]) =>
    categories.filter((c) => c.type === type).map((c) => ({ name, value: c.value, count: c.count })),
  );
  const sum = members.reduce((acc, m) => acc + m.value, 0);
  return members
    .sort((a, b) => b.value - a.value)
    .map((m) => ({
      ...m,
      // Mitglieder ohne Eintrag in `fines` (z. B. Gast) haben keine Farbe
      color: memberColors.value[m.name] ?? '#94A3B8',
      width: sum > 0 ? `${(m.value / sum) * 100}%` : '0%',
    }));
};

const slices = computed(() =>
  [...props.categories]
    .filter((c) => c.value > 0)
    .sort((a, b) => b.value - a.value)
    .map((c, index) => ({
      index,
      type: c.type,
      value: c.value,
      color: colors.value[c.type],
      width: `${(c.value / total.value) * 100}%`,
      share: `${Math.round((c.value / total.value) * 100)} %`,
      members: membersOf(c.type),
    })),
);

const ariaLabel = computed(() => 'Anteile: ' + slices.value.map((s) => `${s.type} ${s.share}`).join(', '));

const { active, root, hint, hoverOpen, hoverClose, toggle } = useDetailPopover(() => props.categories);

const grown = useGrow(() => props.categories);
</script>
